import React, { useState } from 'react';
import { X, Copy, Check, Linkedin, Link2 } from 'lucide-react';
import type { Badge } from '@/types/achievement';
import { useUser } from '@/contexts/UserContext';
// import { getEncryptedUserId } from '@/utils/encryption';

interface BadgeSharePopupProps {
  badge: Badge | null;
  isOpen: boolean;
  onClose: () => void;
}

const BadgeSharePopup: React.FC<BadgeSharePopupProps> = ({ 
  badge, 
  isOpen, 
  onClose 
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !badge) return null;

  const { user } = useUser();
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://dev-api.skillsture.io';
  // const encryptedUserId = user?.id ? getEncryptedUserId(user.id.toString()) : '0';
  // const credentialLink = `${baseUrl}/profile/${encryptedUserId}/badges/${badge.id}`;
  // const credentialLink = `${baseUrl}/profile/${user?.id}/badges/${badge.id}`;
  const credentialLink = `${baseUrl}/profile/${user?.id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(credentialLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleShareLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(credentialLink)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-blue-950 to-slate-800 border border-blue-300/40 outline-slate-600 rounded-xl max-w-md w-full p-6 relative text-base text-slate-400 text-start">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white bg-transparent transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <h2 className="text-lg font-semibold text-white mb-6">
          Share Your Badge
        </h2>

        {/* Badge Image & Name */}
        <div className="flex sm:flex-row sm:justify-between flex-col justify-center items-center mb-6 p-4 bg-slate-800/50 rounded outline outline-slate-700">
          <img 
            className="flex h-auto max-w-16 min-w-16 object-center" 
            src={badge.image || badge.image_url || '/pages/achievements/Digital-Badges.png'} 
            alt={badge.name} onError={(e) => e.currentTarget.src = '/pages/achievements/Digital-Badges.png'}
          />
          <div className="flex flex-grow flex-col sm:items-start items-center ps-3">
            <span className="text-lg font-normal text-white mb-1">{badge.name}</span>
            <span className="font-sm">{badge.pathway as string}</span>
          </div>
        </div>
        {/* Shareable Link */}
        <div className="flex sm:flex-col sm:justify-between flex-col justify-start items-start mb-6 p-4 bg-blue-900/20 rounded outline  outline-blue-500/30">
          <span className="block text-blue-300 text-base font-normal mb-2">
            <Link2 size={20} className="inline mr-3 -mt-0.5" />
            Shareable Credential Link
          </span>
          <span className="w-full my-2 text-sm break-words text-wrap font-normal text-blue-200 hover:text-blue-300 transition-colors">
            {credentialLink}
          </span>
          <button
            onClick={handleCopyLink}
            className="w-full mt-2 bg-white rounded-lg outline outline-1 hover:bg-white/70 text-blue-500 py-2 font-medium transition-colors border border-blue-500/30 outline-blue-500/30"
          >
            {copied ? <Check size={20} className="inline mr-3 -mt-1" /> : <Copy size={20} className="inline mr-3 -mt-1" />}
            {copied ? "Link Copied" : "Copy Link"}
          </button>
        </div>

        {/* Share Options */}
        <div className="mb-1">
          <p className="text-white/80 text-base mb-3">
            Share this badge
          </p>
          <button
            onClick={handleShareLinkedIn}
            className="w-1/2 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-normal transition-colors"
          >
            <Linkedin size={20} className="mr-3" />
            LinkedIn
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeSharePopup;